import React, { useState, useEffect } from "react";
import { Modal, Tabs, Tab, Spinner, Alert, Button } from "react-bootstrap";
import { FiTrash2, FiDownload } from "react-icons/fi";
import instance from "../../../services/baseService";
import toast from "react-hot-toast";

const ManualInwardOutwardModal = ({ show, onHide, clientId, reportId, port_code_id }) => {
    const [activeTab, setActiveTab] = useState("");
    const [letterData, setLetterData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);


    useEffect(() => {
        if (show) {
            fetchLetterData();
        }
    }, [show, clientId, reportId, port_code_id]);

    const fetchLetterData = async () => {
        setLoading(true);
        setError("");

        try {
            const response = await instance.get(
                `/api/v1/reconciliation/manual/letters/${clientId}/${reportId}/${port_code_id}` // Adjust endpoint as needed
            );

            if (response.data && response.data.type === 'success') {
                const data = response.data.data;

                if (data && data.length > 0) {
                    setLetterData(data);
                    // Set first tab as active
                    setActiveTab(data[0].type);
                } else {
                    setLetterData([]);
                    setActiveTab("");
                }
            } else {
                throw new Error(response.data?.message || 'Failed to fetch letters');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError(error.response?.data?.message || error.message || 'Failed to load letters');
            setLetterData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleDelete = async (letterId, type) => {
        if (!window.confirm(`Are you sure you want to delete the ${type} letter?`)) {
            return;
        }

        setDeleting(true);

        try {
            const response = await instance.post(
                `/api/v1/reconciliation/manual/letters/delete/${letterId}`
            );

            if (response.data && response.data.type === 'success') {
                toast.success(response.data.message);
                onHide();
            } else {
                throw new Error(response.data?.message || 'Failed to delete letter');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Deletion failed");
            console.error('Delete error:', error);
        } finally {
            setDeleting(false);
        }
    };

    const renderPDFViewer = (pdfUrl) => {
        if (!pdfUrl) {
            return (
                <Alert variant="info" className="text-center my-5">
                    <Alert.Heading>No Document Available</Alert.Heading>
                    <p className="mb-0">No PDF document found for this type.</p>
                </Alert>
            );
        }

        return (
            <div className="pdf-container" style={{ height: '600px', width: '100%' }}>
                <iframe
                    src={pdfUrl}
                    title="PDF Viewer"
                    width="100%"
                    height="100%"
                    style={{ border: 'none', borderRadius: '8px' }}
                />
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading letters...</p>
                </div>
            );
        }

        if (!letterData || letterData.length === 0) {
            return (
                <Alert variant="info" className="text-center my-5">
                    <Alert.Heading>No Data Available</Alert.Heading>
                </Alert>
            );
        }

        return (
            <Tabs
                activeKey={activeTab}
                onSelect={handleTabChange}
                className="mb-3 custom-tabs"
                justify
            >
                {letterData.map((letter) => (
                    <Tab
                        key={letter.type}
                        eventKey={letter.type}
                        title={letter.type.charAt(0).toUpperCase() + letter.type.slice(1)}
                    >
                        <div className="d-flex justify-content-end mb-3">
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(letter.id, letter.type)}
                                disabled={deleting}
                                className="d-flex align-items-center fw-bold"
                            >
                                {deleting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        Delete
                                    </>
                                )}
                            </Button>
                        </div>
                        {renderPDFViewer(letter.pdf_url)}
                    </Tab>
                ))}
            </Tabs>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>Transfer Letters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderContent()}
            </Modal.Body>
        </Modal>
    );
};

export default ManualInwardOutwardModal;